<template>
  <div>
  <v-data-table
    dense
    :headers="headers"
    :items="internalValue"
    :items-per-page="-1"
    hide-default-footer
    class="elevation-1 EFTDetails"
  >
    <template v-slot:top>
      <div class="d-flex justify-end">
        <v-btn
          class="mx-2" color="primary"
          fab icon x-small
          @click="addOneAndFocusIfFirst"
          @keydown.enter.stop.prevent="addOneAndFocusIfFirst"
          v-if="amountToNumber(amountRemaining) !== 0 || internalValue.length === 0"
        >
          <v-icon>
            {{icons.mdiPlus}}
          </v-icon>
        </v-btn>
      </div>
    </template>

    <template v-slot:[`item.EFTType`]="{ item }">
      <v-select
        dense
        class="eft-type"
        ref="type"
        v-model="item.EFTType"
        :items="EFTTypes"
        :rules="eftTypeValidation"
        focus
        @change="somethingChanged"
        item-text="description"
        item-value="name"
      ></v-select>
    </template>

    <template v-slot:[`item.amount`]="{ item }">
      <v-text-field
        dense
        v-model="item.amount"
        :rules="amountValidation"
        single-line
        class="eft-amount"
        @keydown="shortcutHandler"
        @keydown.enter="somethingChanged"
        @blur="somethingChanged()"
      ></v-text-field>
    </template>

    <template v-slot:[`item.actions`]="{ item, index }">
      <v-btn
        v-if="isPayrix && item.EFTType === 'PAYRIX'"
        class="input-text-size mr-2"
        dense
        color="primary"
        @click="handlePayrix(index)"
      >
        Pay with eCheck
      </v-btn>
      <v-icon
        small class="mr-2"
        tabindex="-1"
        @click="removeOne(item)"
      >
        {{icons.mdiDelete}}
      </v-icon>
    </template>

    <template slot="body.append">
      <tr class="blue--text">
          <td colspan="2" id="eft-remaining-amount">
            Total Remaining: ${{ amountFormat(amountRemaining) }}
          </td>
      </tr>
    </template>

  </v-data-table>
  <PayrixPaymentDialog
  v-if="isDialogOpen"
  :fieldNameLists="['Deposit Amount','eCheck Fee']"
  />
</div>
</template>

<script>
import {
  mdiPlus,
  mdiDelete,
} from '@mdi/js';

import { mapState, mapActions } from 'vuex';

import {
  isTabOrPlus,
  focusBySelector,
  amountToNumber,
  amountFormat,
  selectInputTextBySelector,
} from '../../util/shared/vue-global';

import PayrixPaymentDialog from '../common/PayrixPaymentDialog.vue';

const defaultItem = {
  EFTType: undefined,
  amount: 0,
};

export default {
  name: 'EFTDetails',
  components: {
    PayrixPaymentDialog,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    componentKey: Number,
    depositAmount: [Number, String],
    depositData: Object, // parent DepositDetails internalValue
  },
  data: () => ({
    icons: {
      mdiPlus,
      mdiDelete,
    },
    headers: [
      { text: ' EFT Type', value: 'EFTType' },
      { text: 'Amount', value: 'amount' },
      { text: 'Actions', value: 'actions', sortable: false },
    ],
  }),
  async created() {
    await this.loadConfig({ requiredScope: 'odconfig' });
    await this.loadConfig({ requiredScope: 'paymentConfig' });
  },
  computed: {
    ...mapState({
      EFTTypes: (state) => state.enums.ODEFTItemTypeEnum,
      odSettingItem: (state) => state.SystemConfig.odSettingItem,
      paymentSettingItem: (state) => state.SystemConfig.paymentSettingItem || {},
      payrixSuccessFlag: (state) => state.common.payrixPayment.paymentSuccessFlag,
      isDialogOpen: (state) => state.common.payrixPayment.isDialogOpen,
    }),
    internalValue: {
      get() {
        const newVal = JSON.parse(JSON.stringify(this.value));
        const updatedValue = newVal.map(this.loadInitialValues);
        return updatedValue;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    eftTypeValidation() {
      return [
        (value) => !!value || 'Required',
      ];
    },
    amountValidation() {
      return [
        (value) => !!value || 'Required',
        (value) => value !== 0 || 'Should be non-zero',
      ];
    },
    amountRemaining() {
      let totalRemainingAmount = amountToNumber(this.depositAmount);
      for (let i = 0; i < this.internalValue.length; i += 1) {
        totalRemainingAmount -= amountToNumber(this.internalValue[i].amount);
      }
      return totalRemainingAmount;
    },
    isPayrix() {
      const settings = this.odSettingItem;
      const isPayrixGateway = settings.selectedGateway === 'payrix';
      const hasConfig = !!settings.payrixMerchentId && !!settings.payrixApiKey;
      const result = isPayrixGateway && hasConfig;
      return result;
    },
  },
  watch: {
    payrixSuccessFlag: {
      immediate: false,
      handler(val) {
        if (val) {
          this.$emit('payrixSuccess');
        }
      },
    },
  },
  methods: {
    ...mapActions('SystemConfig', ['loadConfig']),
    ...mapActions('common/payrixPayment', [
      'setPaymentContext',
      'setOdDepositData',
      'setPayrixPaymentType',
      'setPayrixPaymentData',
      'toggleDialog',
    ]),
    amountToNumber,
    amountFormat,
    loadInitialValues(item) {
      return {
        ...item,
        amount: amountFormat(amountToNumber(item.amount)),
      };
    },
    shortcutHandler(e) {
      if (isTabOrPlus(e)) {
        const allAmounts = document.querySelectorAll('.eft-amount input');
        const allLastColumns = [...allAmounts];
        if (allAmounts.length > 0) {
          if (allLastColumns[allLastColumns.length - 1].id === e.target.id) {
            let totalAmount = 0;
            allAmounts.forEach((v) => {
              totalAmount += amountToNumber(v.value);
              this.somethingChanged();
            });
            if (amountToNumber(this.depositAmount) !== totalAmount) {
              this.addOne();
              this.$nextTick(() => {
                focusBySelector('.eft-type input', 'last');
                selectInputTextBySelector('.eft-type input', 'last');
              });
            }
          }
        }
      }
    },
    addOneAndFocusIfFirst() {
      this.addOne();
      this.$nextTick(() => {
        const allAmounts = document.querySelectorAll('.eft-amount input');
        if (allAmounts.length === 1) {
          focusBySelector('.eft-type input', 'last');
          selectInputTextBySelector('.eft-type input', 'last');
        }
      });
    },
    addOne() {
      const allAmounts = document.querySelectorAll('.cash-check-amount input');
      const lastAmount = allAmounts[allAmounts.length - 1]
        ? allAmounts[allAmounts.length - 1].value : 0;
      this.$emit('input', [
        ...this.internalValue,
        {
          ...defaultItem,
          amount: amountFormat(amountToNumber(lastAmount)),
        },
      ]);
      return true;
    },
    removeOne(item) {
      this.$emit('input', [
        ...this.internalValue.filter((candidate) => item !== candidate),
      ]);
    },
    somethingChanged() {
      this.$emit('input', this.internalValue);
    },
    async handlePayrix(index) {
      const amt = amountToNumber(this.internalValue[index].amount);
      if (amt && amt > 0) {
        const creditFeePercentage = amountToNumber(this.paymentSettingItem.payrixCreditPercentage);
        const debitFeePercentage = amountToNumber(this.paymentSettingItem.payrixDebitPercentage);
        const debitFeeFlat = amountToNumber(this.paymentSettingItem.payrixDebitFeeFlat);
        const debitAmtLimit = amountToNumber(this.paymentSettingItem.payrixDebitAmtLimit);
        const convenienceFeeCredit = amountToNumber((amt * creditFeePercentage) / 100);
        const totalPlusCreditFee = amountToNumber(amt + convenienceFeeCredit);
        const eCheckFee = amountToNumber(this.paymentSettingItem.payrixEcheckFee);
        const totalPlusEcheckFee = amountToNumber(amt + eCheckFee);

        let convenienceFeeDebit = 0;

        if (debitAmtLimit > 0 && amt < debitAmtLimit) {
          convenienceFeeDebit = debitFeeFlat;
        } else {
          convenienceFeeDebit = amountToNumber((amt * debitFeePercentage) / 100);
        }
        const totalPlusDebitFee = amountToNumber(amt + convenienceFeeDebit);
        const payrixPaymentData = {
          totalAmount: amt,
          convenienceFeeCredit,
          totalPlusCreditFee,
          convenienceFeeDebit,
          totalPlusDebitFee,
          creditFeePercentage,
          debitFeePercentage,
          debitFeeFlat,
          eCheckFee,
          totalPlusEcheckFee,
        };
        await this.setPaymentContext('OD');
        await this.setOdDepositData(this.depositData);
        await this.setPayrixPaymentType('ECHECK');
        await this.setPayrixPaymentData(payrixPaymentData);
        await this.toggleDialog(true);
      } else {
        alert('Please enter a valid amount');
      }
    },
  },
};
</script>

<style lang="sass">
  .eft-amount input
    text-align: right
</style>
